import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Free OpenStreetMap + Leaflet. No API key. Selected stops are numbered blue teardrops
// (always visible); unselected locations are navy dots grouped into clusters. Clicking a
// cluster zooms in (it never selects the members); clicking a single pin calls onPick(id).
const FL_CENTER = [27.9, -81.7];

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const dotIcon = () => L.divIcon({
  className: '',
  html: '<div style="background:#0E2A47;width:20px;height:20px;border-radius:50%;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);cursor:pointer"></div>',
  iconSize: [20, 20], iconAnchor: [10, 10],
});
const numIcon = (order) => L.divIcon({
  className: '',
  html: `<div style="background:#1C4FC9;color:#fff;width:40px;height:40px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 7px rgba(0,0,0,.5);cursor:pointer"><span style="transform:rotate(45deg);font-size:17px;font-weight:800;line-height:1">${order}</span></div>`,
  iconSize: [40, 40], iconAnchor: [20, 40],
});

const popupHtml = (p) => `<div style="min-width:170px">
  <div style="font-weight:700;color:#0E2A47">${esc(p.customer_name || p.label || '')}</div>
  ${p.customer_name ? `<div style="font-size:12px;color:#334155">${esc(p.label || '')}</div>` : ''}
  <div style="font-size:11px;color:#64748B;font-family:monospace">#${esc(p.account_number || '—')}</div>
  ${p.address ? `<div style="font-size:12px;color:#475569;margin-top:2px">${esc(p.address)}</div>` : ''}
  <div style="font-size:11px;color:#1C4FC9;margin-top:4px">${p.order ? `Stop #${p.order} — click pin to remove` : 'Click pin to add as a stop'}</div>
</div>`;

const RouteMap = ({ points = [], onPick, focusId, fitSignature = '', heightClass = 'h-[420px]' }) => {
  const node = useRef(null);
  const map = useRef(null);
  const cluster = useRef(null);     // unselected dots
  const selLayer = useRef(null);    // selected numbered pins (never clustered)
  const byId = useRef({});
  const lastFit = useRef(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    if (map.current) return;
    map.current = L.map(node.current, { scrollWheelZoom: true }).setView(FL_CENTER, 7);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
    }).addTo(map.current);
    cluster.current = L.markerClusterGroup({
      showCoverageOnHover: false, maxClusterRadius: 50,
      spiderfyOnMaxZoom: true, zoomToBoundsOnClick: false, disableClusteringAtZoom: 13,
    });
    // Cluster click reveals nearby pins without an excessive zoom that hides the area.
    cluster.current.on('clusterclick', (a) => {
      map.current.fitBounds(a.layer.getBounds().pad(0.4), { maxZoom: 13, animate: true });
    });
    map.current.addLayer(cluster.current);
    selLayer.current = L.layerGroup().addTo(map.current);
    setTimeout(() => map.current && map.current.invalidateSize(), 150);
  }, []);

  useEffect(() => {
    if (!map.current) return;
    cluster.current.clearLayers();
    selLayer.current.clearLayers();
    byId.current = {};
    const latlngs = [];
    points.forEach((p) => {
      if (p.lat == null || p.lng == null) return;
      const marker = L.marker([p.lat, p.lng], { icon: p.order ? numIcon(p.order) : dotIcon() });
      marker.bindPopup(popupHtml(p));
      marker.on('click', () => onPickRef.current && onPickRef.current(p.id));
      byId.current[p.id] = { marker, selected: !!p.order };
      if (p.order) selLayer.current.addLayer(marker);
      else cluster.current.addLayer(marker);
      latlngs.push([p.lat, p.lng]);
    });
    // Fit only when the displayed SET changes (filters), not on every selection.
    if (fitSignature !== lastFit.current) {
      lastFit.current = fitSignature;
      setTimeout(() => {
        if (!map.current) return;
        map.current.invalidateSize();
        if (latlngs.length === 1) map.current.setView(latlngs[0], 14);
        else if (latlngs.length > 1) map.current.fitBounds(latlngs, { padding: [40, 40] });
        else map.current.setView(FL_CENTER, 7);
      }, 60);
    }
  }, [points, fitSignature]);

  // Pan to / highlight a specific location (e.g. selected from the list).
  useEffect(() => {
    if (!focusId || !map.current) return;
    const entry = byId.current[focusId];
    if (!entry) return;
    const open = () => entry.marker.openPopup();
    if (entry.selected) { map.current.panTo(entry.marker.getLatLng()); open(); }
    else { cluster.current.zoomToShowLayer(entry.marker, open); }
  }, [focusId]);

  return <div ref={node} className={`w-full rounded-xl border border-slate-200 bg-slate-100 ${heightClass}`} data-testid="route-map" />;
};

export default RouteMap;
